// Indonesian labels for the PBJ identifiers (Non-Penyedia, migration 0023).
import type { PbjMethod, PbjProject, PbjRoute, PbjStage, SwakelolaType } from "../api";

/** The seven stages; Rencana Kerja & Anggaran PBJ (key identifikasi_kebutuhan) is stage 0, so a stage's number is its index. */
export const PBJ_STAGE_ORDER: PbjStage[] = [
  "identifikasi_kebutuhan",
  "perencanaan",
  "persiapan_pengadaan",
  "persiapan_pemilihan",
  "pelaksanaan_pemilihan",
  "pelaksanaan_kontrak",
  "serah_terima",
];

export const PBJ_STAGE_LABEL: Record<PbjStage, string> = {
  identifikasi_kebutuhan: "Rencana Kerja & Anggaran PBJ",
  perencanaan: "Perencanaan",
  persiapan_pengadaan: "Persiapan Pengadaan",
  persiapan_pemilihan: "Persiapan Pemilihan",
  pelaksanaan_pemilihan: "Pelaksanaan Pemilihan",
  pelaksanaan_kontrak: "Pelaksanaan Kontrak",
  serah_terima: "Serah Terima",
};

export const PBJ_STAGE_HINT: Record<PbjStage, string> = {
  identifikasi_kebutuhan: "Kebutuhan sesuai Rencana Kerja, spesifikasi atau KAK, ketersediaan pasar, dan RAB usulan anggaran oleh PA/KPA.",
  perencanaan: "Telaah RKA, strategi, pemaketan, dan RUP.",
  persiapan_pengadaan: "Spesifikasi atau KAK, HPS, dan rancangan kontrak.",
  persiapan_pemilihan: "Reviu paket, dokumen, dan strategi pemilihan.",
  pelaksanaan_pemilihan: "Pengumuman, penawaran, evaluasi, dan penetapan.",
  pelaksanaan_kontrak: "Penandatanganan, pengendalian, dan pembayaran.",
  serah_terima: "Pemeriksaan hasil, BAST, dan penyerahan.",
};

export const PBJ_ROUTE_LABEL: Record<PbjRoute, string> = {
  penyedia: "Melalui Penyedia",
  swakelola: "Swakelola",
};

export const PBJ_METHOD_ORDER: PbjMethod[] = ["e_purchasing", "pengadaan_langsung", "penunjukan_langsung", "tender_cepat", "tender_seleksi"];

export const PBJ_METHOD_LABEL: Record<PbjMethod, string> = {
  e_purchasing: "E-Purchasing",
  pengadaan_langsung: "Pengadaan Langsung",
  penunjukan_langsung: "Penunjukan Langsung",
  tender_cepat: "Tender Cepat",
  tender_seleksi: "Tender / Seleksi",
};

export const SWAKELOLA_ORDER: SwakelolaType[] = ["tipe_1", "tipe_2", "tipe_3", "tipe_4"];

export const SWAKELOLA_LABEL: Record<SwakelolaType, string> = {
  tipe_1: "Swakelola Tipe I",
  tipe_2: "Swakelola Tipe II",
  tipe_3: "Swakelola Tipe III",
  tipe_4: "Swakelola Tipe IV",
};

/** "Melalui Penyedia, Tender Cepat" or "Swakelola Tipe II". */
export function routeText(p: Pick<PbjProject, "route" | "method" | "swakelolaType">): string {
  if (p.route === "swakelola") return p.swakelolaType ? SWAKELOLA_LABEL[p.swakelolaType] : PBJ_ROUTE_LABEL.swakelola;
  return p.method ? `${PBJ_ROUTE_LABEL.penyedia}, ${PBJ_METHOD_LABEL[p.method]}` : PBJ_ROUTE_LABEL.penyedia;
}

/** The Alur PBJ diagram of a route's own path (data/pbj/pbj-seed.json codes, as modules/pbjflow). */
export const PBJ_METHOD_DIAGRAM: Record<PbjMethod, string> = {
  e_purchasing: "M01_EPURCHASING",
  pengadaan_langsung: "M02_LANGSUNG",
  penunjukan_langsung: "M03_PENUNJUKAN",
  tender_cepat: "M04_TENDER_CEPAT",
  tender_seleksi: "M05_TENDER_SELEKSI",
};

export const SWAKELOLA_DIAGRAM: Record<SwakelolaType, string> = {
  tipe_1: "S01_TIPE_I",
  tipe_2: "S02_TIPE_II",
  tipe_3: "S03_TIPE_III",
  tipe_4: "S04_TIPE_IV",
};

/** The diagram of each of the seven stages (modules/pbjflow STAGE_DIAGRAM). */
export const PBJ_STAGE_DIAGRAM: Record<PbjStage, string> = {
  identifikasi_kebutuhan: "00_KEBUTUHAN",
  perencanaan: "01_RENCANA",
  persiapan_pengadaan: "02_PERSIAPAN_PENGADAAN",
  persiapan_pemilihan: "03_PERSIAPAN_PEMILIHAN",
  pelaksanaan_pemilihan: "04_PELAKSANAAN_PEMILIHAN",
  pelaksanaan_kontrak: "05_KONTRAK",
  serah_terima: "06_SERAH_TERIMA",
};

/** Alur PBJ on a diagram, with the project whose ticks it marks as done. */
export function flowPath(code: string | null, projectId?: string): string {
  const q = new URLSearchParams();
  if (code) q.set("diagram", code);
  if (projectId) q.set("project", projectId);
  const qs = q.toString();
  return qs ? `/alur-pbj?${qs}` : "/alur-pbj";
}

/** Alur PBJ opened on the diagram of the project's method or Swakelola type, marking that project's ticks. */
export function routeFlowPath(p: Pick<PbjProject, "route" | "method" | "swakelolaType"> & { id?: string }): string {
  const code = p.route === "swakelola" ? (p.swakelolaType ? SWAKELOLA_DIAGRAM[p.swakelolaType] : null) : p.method ? PBJ_METHOD_DIAGRAM[p.method] : null;
  return flowPath(code, p.id);
}

/**
 * The diagram of a stage for a route (reference stage-flow): the stage's own
 * diagram, except a Swakelola stage cut from its type's diagram.
 */
export function stageFlowPath(p: Pick<PbjProject, "id" | "route" | "swakelolaType">, stage: PbjStage): string {
  const swakelolaCut = p.route === "swakelola" && stage !== "identifikasi_kebutuhan" && stage !== "perencanaan" && p.swakelolaType;
  return flowPath(swakelolaCut ? SWAKELOLA_DIAGRAM[p.swakelolaType!] : PBJ_STAGE_DIAGRAM[stage], p.id);
}

/** "Tender / Seleksi" or "Swakelola Tipe II": the method or type alone. */
export function methodText(p: Pick<PbjProject, "route" | "method" | "swakelolaType">): string {
  if (p.route === "swakelola") return p.swakelolaType ? SWAKELOLA_LABEL[p.swakelolaType] : PBJ_ROUTE_LABEL.swakelola;
  return p.method ? PBJ_METHOD_LABEL[p.method] : PBJ_ROUTE_LABEL.penyedia;
}

/** False for a stage the route has no activities in: Swakelola has no pemilihan (as the API's stageApplies). */
export function pbjStageApplies(route: PbjRoute, stage: PbjStage): boolean {
  return route === "penyedia" || (stage !== "persiapan_pemilihan" && stage !== "pelaksanaan_pemilihan");
}

/** What a "Di luar Pluto" activity is: done by another party, never ticked here, never counted. */
export const OUTSIDE_PLUTO_HINT = "Dikerjakan oleh penyedia, peserta, sistem, atau pihak lain (di SPSE, katalog elektronik, atau instansinya), bukan oleh peran di Pluto. Tidak dicentang di sini dan tidak dihitung dalam progres.";

/** Whole percent of done over total; 0 when there is nothing to do. */
export function progressPct(done: number, total: number): number {
  return total > 0 ? Math.round((done / total) * 100) : 0;
}

/** The diagram groups of the dataset, in the order Alur PBJ lists them. */
export const DIAGRAM_GROUPS = ["Tahap Utama", "Metode Pemilihan", "Swakelola Tipe", "Grand Flowchart"] as const;

export const DIAGRAM_GROUP_LABEL: Record<string, string> = {
  "Tahap Utama": "Tahap utama",
  "Metode Pemilihan": "Metode pemilihan",
  "Swakelola Tipe": "Swakelola",
  "Grand Flowchart": "Diagram menyeluruh",
};

export const NODE_TYPE_LABEL: Record<string, string> = {
  proses: "Kegiatan",
  keputusan: "Keputusan",
  awal: "Awal",
  akhir: "Akhir",
};
